//@flow
import {Link, useHistory} from 'react-router-dom';
import {
  CircularProgress,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {ArrowBack, HelpOutline} from '@material-ui/icons';
import {colors} from '../../../../../../assets/styles/colors';
import PopupBannerLayout from './PopupBannerLayout';
import PopupBannerImages from './PopupBannerImages';
import {FormProvider, useForm} from 'react-hook-form';
import {
  POPUP_BANNER_FIELDS,
  POPUP_BANNER_LAYOUTS,
  POPUP_BUTTON_ACTION,
  POPUP_TRIGGER_TYPE,
  POPUP_TRIGGER_VALUE_CRITERIA_TYPE,
} from './constants';
import PopupBannerTitles from './PopupBannerTitles';
import PopupBannerMainButton from './PopupBannerMainButton';
import PopupBannerTrigger from './PopupBannerTrigger';
import PopupBannerEditor from './PopupBannerEditor';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import ConfirmModal from '../../../../../../elements/Modals/ConfirmModal';
import {getPopupBannerBlobImages} from './utils';
import {usePopupBannerActions} from '../../../../utils/popup-banner';
import {useToastActions} from '../../../../../toast-alert/utils';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '1200px',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,.5)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    padding: '.75rem 0',
  },
  title: {
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',

    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '70% 30%',
    },
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '1rem',
    position: 'relative',
    padding: '1rem',
    borderRadius: '12px',
    background: 'white',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
  },
  rightContent: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      height: 'min-content',
      position: 'sticky',
      top: '5.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
    },
  },
  submitButton: {
    display: 'block',
    maxWidth: '200px',
    margin: '0 auto',
  },
  captionText: {
    fontSize: '0.875rem',
    color: colors.grey600,
    marginBottom: '.25rem',
    fontWeight: 500,
  },
}));

export default function PopupBannerForm() {
  const classes = useStyles();

  const {createBanner, loading} = usePopupBannerActions();
  const {showToast} = useToastActions();

  const history = useHistory();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const methods = useForm({
    defaultValues: {
      [POPUP_BANNER_FIELDS.CONTENT_POSITION]: POPUP_BANNER_LAYOUTS.BELOW,
      [POPUP_BANNER_FIELDS.BUTTON_ACTION]: POPUP_BUTTON_ACTION.CLOSE,
      [POPUP_BANNER_FIELDS.TRIGGER_TYPE]: POPUP_TRIGGER_TYPE.TIME_PASSED,
      [POPUP_BANNER_FIELDS.TRIGGER_VALUE_CRITERIA_TYPE]:
        POPUP_TRIGGER_VALUE_CRITERIA_TYPE.FIXED,
    },
  });

  const {register} = methods;

  useEffect(() => {
    register(POPUP_BANNER_FIELDS.IMAGE);
    // register(POPUP_BANNER_FIELDS.TITLE);
    // register(POPUP_BANNER_FIELDS.SUBTITLE);
    register(POPUP_BANNER_FIELDS.BACKGROUND_COLOR);
    register(POPUP_BANNER_FIELDS.TEXT_COLOR);
    register(POPUP_BANNER_FIELDS.CONTENT_POSITION);
    // register(POPUP_BANNER_FIELDS.BUTTON_TEXT);
    register(POPUP_BANNER_FIELDS.BUTTON_ACTION);
    register(POPUP_BANNER_FIELDS.BUTTON_URL_TYPE);
    register(POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER);
    register(POPUP_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR);
    register(POPUP_BANNER_FIELDS.BUTTON_TEXT_COLOR);
    // register(POPUP_BANNER_FIELDS.TRIGGER_VALUE);
    register(POPUP_BANNER_FIELDS.TRIGGER_TYPE);
    register(POPUP_BANNER_FIELDS.TRIGGER_VALUE_CRITERIA_TYPE);
  }, [register]);

  const getErrorMessage = (errorCode: string, defaultMessage: string) => {
    const errorMessages = {
      MaxBannerPopupsLimitExceeded:
        'Has alcanzado el límite de 5 popups por tienda',
      MalformedBannerButtonObjectIdentifier:
        'Debes proporcionar una URL válida',
    };
    return errorMessages[errorCode] || defaultMessage;
  };

  const onSubmit = async data => {
    try {
      const image = data[POPUP_BANNER_FIELDS.IMAGE];
      let result;

      if (!image) {
        result = await createBanner({
          ...data,
        });
      } else {
        const blobResult = await getPopupBannerBlobImages(
          data[POPUP_BANNER_FIELDS.CONTENT_POSITION]
        );
        if (!blobResult) return;

        result = await createBanner({
          ...data,
          buttonText: data[POPUP_BANNER_FIELDS.BUTTON_TEXT] || undefined,
          bannerBlob: blobResult,
        });
      }

      if (result) {
        showToast({
          type: 'success',
          title: 'Éxito',
          message: 'Banner creado correctamente',
          duration: 3000,
        });
        history.push('/my-store/pages/home-page');
      }
    } catch (error) {
      const errorCode = error?.error?.error || 'UNKNOWN_ERROR';
      const errorMessage =
        error?.error?.message || 'Ocurrió un error al crear el banner';
      const displayMessage = getErrorMessage(errorCode, errorMessage);

      showToast({
        type: 'danger',
        title: 'Error',
        message: displayMessage,
        duration: 5000,
      });
    }
  };

  return (
    <div className={classes.root}>
      {loading && (
        <div className={classes.loader}>
          <CircularProgress size={40} />
        </div>
      )}
      <header className={classes.header}>
        <IconButton
          className={classes.backButton}
          component={Link}
          to="/my-store/pages/home-page"
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.title}>Crea un banner</Typography>
      </header>

      <FormProvider {...methods}>
        <form
          className={classes.container}
          onSubmit={e => {
            e.preventDefault();
            openConfirmModal();
          }}
        >
          <div className={classes.leftContent}>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                1. Selecciona una estructura de banner
              </Typography>
              <PopupBannerLayout />
            </section>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                2. Sube una imágen
              </Typography>
              <div>
                <Typography className={classes.captionText}>
                  Máx. 1 MB por imagen. Archivos más grandes se comprimirán.
                </Typography>
                <PopupBannerImages />
              </div>
            </section>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                3. Define el título, subtítulo y el color del fondo y texto
              </Typography>
              <PopupBannerTitles />
            </section>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                4.Cofigura tu botón
              </Typography>
              <PopupBannerMainButton />
            </section>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                5.Tiempo de apertura (El banner pop up aparecerá despues del
                tiempo determinado)
              </Typography>
              <PopupBannerTrigger />
            </section>
            <section className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                6. Acomoda la imagen del banner
              </Typography>
              <PopupBannerEditor />
            </section>
            <footer className={classes.actions}>
              <CustomButton
                className={classes.submitButton}
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
              >
                Guardar
              </CustomButton>
            </footer>
          </div>
          <div className={classes.rightContent}></div>
        </form>
      </FormProvider>

      <ConfirmModal
        open={confirmModalOpen}
        title="Antes de guardar el banner"
        message="Asegúrate de estar satisfecho con el resultado, ya que no podrás modificarlo una vez guardado."
        onCancel={closeConfirmModal}
        onClose={closeConfirmModal}
        icon={<HelpOutline fontSize="large" />}
        onConfirm={() => {
          methods.handleSubmit(onSubmit)();
          closeConfirmModal();
        }}
      />
    </div>
  );
}
